import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  KeyRound, BedDouble, Wrench, Car, Clock, MessageSquare,
  Send, CheckCircle2, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUEST_OPTIONS = [
  { key: 'CHECKIN',       label: 'Check-in / access help', icon: KeyRound, hint: 'Lockbox, keys, arrival.' },
  { key: 'HOUSEKEEPING',  label: 'Housekeeping request',   icon: BedDouble, hint: 'Towels, cleaning, linens.' },
  { key: 'MAINTENANCE',   label: 'Maintenance issue',      icon: Wrench,   hint: 'WiFi, A/C, plumbing.' },
  { key: 'TRANSPORT',     label: 'Transport request',      icon: Car,      hint: 'Airport, taxi, tours.' },
  { key: 'LATE_CHECKOUT', label: 'Late checkout',          icon: Clock,    hint: 'Stay a bit longer.' },
  { key: 'OTHER',         label: 'Other request',          icon: MessageSquare, hint: 'Anything else we can help with.' },
];

const GuestHubRequestPage = () => {
  const { propertySlug } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [form, setForm] = useState({ guest_name: '', details: '', when_needed: '', contact: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ackResponse, setAckResponse] = useState(null);

  useEffect(() => {
    axios.get(`${API}/properties/slug/${propertySlug}`)
      .then((res) => setProperty(res.data))
      .catch(() => setError('We couldn\u2019t find that property.'))
      .finally(() => setLoading(false));
  }, [propertySlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedType) return;
    if (!form.guest_name.trim() || !form.details.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/guesthub/${propertySlug}/request`, {
        request_type: selectedType,
        guest_name: form.guest_name.trim(),
        details: form.details.trim(),
        when_needed: form.when_needed || null,
        contact: form.contact || null,
      });
      setAckResponse(res.data || null);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] p-6">
        <div className="max-w-xl mx-auto space-y-6">
          <Skeleton className="h-24 w-full rounded-2xl" />
          <Skeleton className="h-10 w-3/4" />
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (error && !property) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] grid place-items-center p-6">
        <Card className="max-w-md w-full border-stone-200">
          <CardContent className="p-8 text-center">
            <p className="text-stone-900 font-medium">{error}</p>
            <p className="text-sm text-stone-500 mt-2">Please reach out to your host directly.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    const isAutoAcked = !!ackResponse?.auto_acknowledged;
    const isAfterHoursUrgent = !!ackResponse?.after_hours_urgent;
    const ackBody = ackResponse?.auto_ack?.body;
    const escalationPhone = ackResponse?.auto_ack?.escalationPhone;
    return (
      <div className="min-h-screen bg-[#FDFBF7] grid place-items-center p-6">
        <Card className="max-w-md w-full border-stone-200" data-testid="guesthub-success">
          <CardContent className="p-10 text-center">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-heading text-stone-900">Thanks!</h1>
            <p className="text-stone-600 mt-3">We've received your request. Our team will follow up shortly.</p>

            {isAutoAcked && ackBody && (
              <div
                className="mt-6 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-left"
                data-testid="guesthub-afterhours-ack"
              >
                <p className="text-[10.5px] tracking-[0.22em] uppercase text-amber-700 font-medium mb-2">
                  After-hours note
                </p>
                <p className="text-[14px] text-stone-800 leading-relaxed">{ackBody}</p>
                {escalationPhone && (
                  <p className="text-[12.5px] text-stone-600 mt-3">
                    Urgent?{' '}
                    <a
                      href={`tel:${escalationPhone.replace(/\s/g, '')}`}
                      className="text-amber-800 underline"
                      data-testid="guesthub-afterhours-phone"
                    >
                      Call {escalationPhone}
                    </a>
                  </p>
                )}
              </div>
            )}

            {isAfterHoursUrgent && (
              <div
                className="mt-6 rounded-xl border border-red-200 bg-red-50/70 p-4 text-left"
                data-testid="guesthub-afterhours-urgent"
              >
                <p className="text-[10.5px] tracking-[0.22em] uppercase text-red-700 font-medium mb-2">
                  Marked urgent
                </p>
                <p className="text-[14px] text-stone-800 leading-relaxed">
                  We've flagged this as urgent so the on-call team sees it right away. If you can't wait, please call the property directly.
                </p>
              </div>
            )}

            <Button
              variant="outline"
              className="mt-6 rounded-full"
              onClick={() => {
                setSubmitted(false);
                setSelectedType(null);
                setAckResponse(null);
                setForm({ guest_name: '', details: '', when_needed: '', contact: '' });
              }}
              data-testid="guesthub-submit-another"
            >
              Send another request
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="guesthub-public-page">
      <div className="max-w-xl mx-auto p-5 sm:p-8 space-y-6">
        {/* Header */}
        <div className="text-center pt-4">
          <p className="text-xs uppercase tracking-widest text-[#4A5D4F] font-semibold">Welcome to</p>
          <h1 className="text-3xl sm:text-4xl font-heading text-stone-900 mt-1">{property?.name}</h1>
          <p className="text-stone-500 mt-2">How can we help?</p>
        </div>

        {/* Request type tiles */}
        <div>
          <Label className="text-sm font-medium text-stone-700">What do you need?</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2">
            {REQUEST_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const active = selectedType === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setSelectedType(opt.key)}
                  data-testid={`guesthub-type-${opt.key.toLowerCase()}`}
                  className={`text-left p-4 rounded-xl border transition-all ${
                    active
                      ? 'border-[#4A5D4F] bg-[#4A5D4F]/5 shadow-sm'
                      : 'border-stone-200 hover:border-stone-300 bg-white'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${active ? 'bg-[#4A5D4F] text-white' : 'bg-stone-100 text-stone-600'}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-stone-900">{opt.label}</p>
                      <p className="text-xs text-stone-500 mt-0.5">{opt.hint}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Form */}
        {selectedType && (
          <Card className="border-stone-200">
            <CardContent className="p-5 space-y-4">
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="guesthub-form">
                <div>
                  <Label htmlFor="guest_name" className="text-xs text-stone-600">Your name *</Label>
                  <Input
                    id="guest_name"
                    value={form.guest_name}
                    onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                    placeholder="e.g. Sarah Johnson"
                    required
                    data-testid="guesthub-name"
                  />
                </div>
                <div>
                  <Label htmlFor="details" className="text-xs text-stone-600">Request details *</Label>
                  <Textarea
                    id="details"
                    value={form.details}
                    onChange={(e) => setForm({ ...form, details: e.target.value })}
                    placeholder="Tell us what you need."
                    rows={4}
                    required
                    data-testid="guesthub-details"
                  />
                </div>
                <div>
                  <Label htmlFor="when_needed" className="text-xs text-stone-600">When do you need this?</Label>
                  <Input
                    id="when_needed"
                    value={form.when_needed}
                    onChange={(e) => setForm({ ...form, when_needed: e.target.value })}
                    placeholder="e.g. Right now, In 1 hour, Tomorrow morning"
                    data-testid="guesthub-when"
                  />
                </div>
                <div>
                  <Label htmlFor="contact" className="text-xs text-stone-600">Phone or email (optional)</Label>
                  <Input
                    id="contact"
                    value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    placeholder="We'll use this to follow up"
                    data-testid="guesthub-contact"
                  />
                </div>
                {error && (
                  <p className="text-sm text-red-600">{error}</p>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#4A5D4F] hover:bg-[#3d4d42] rounded-full h-11"
                  data-testid="guesthub-submit"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  {submitting ? 'Sending\u2026' : 'Send request'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-stone-400 pt-4">
          Powered by Maris Desk
        </p>
      </div>
    </div>
  );
};

export default GuestHubRequestPage;
